import React, { useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { track } from '@betterment/js-runtime';
import { Box, Button, Card, Heading, Separator, Text } from '@betterment/samba-react';

import { InvestableAssetsDocument } from './graphql/query.investableAssets.generated';

import { CompactCardLoader } from '../../../Shared/components-v2/shimmer-loaders/compact-card-loader';

type Props = {
  accountId: string;
};

const InvestableAssetsCard: React.FC<Props> = ({ accountId }) => {
  const { data } = useGraphQLQuery(InvestableAssetsDocument, {
    variables: { accountId },
    throwOnError: true,
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const handleShowMore = () => {
    setIsExpanded(true);
    track('ElementClicked', {
      location: 'Account',
      module: 'InvestableAssets',
      name: 'ShowMore',
    });
  };

  if (!data) return <CompactCardLoader />;

  if (!data.financialProfile) return null;

  const { account } = data;

  if (account.__typename !== 'DirectTradingAccount') {
    return null;
  }

  const { directTradingInvestableAssetsAdvice } = data.financialProfile;

  if (!directTradingInvestableAssetsAdvice?.advice) return null;

  const { advice, expandableAdvice } = directTradingInvestableAssetsAdvice;
  const hasExpandableContent = Boolean(expandableAdvice);

  return (
    <Card>
      <Box
        css={{
          paddingTop: 'space.300',
          paddingX: 'space.300',
          display: 'flex',
          flexDirection: 'column',
          gap: 'space.300',
        }}
      >
        <Heading as="h2" variant="title200">
          Investable assets
        </Heading>
        <Separator />
      </Box>
      <Box css={{ padding: 'space.300' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
          <Text variant="text50">{advice}</Text>

          {hasExpandableContent && !isExpanded && (
            <Box>
              <Button variant="textPrimary" onClick={handleShowMore}>
                <Text variant="text50">Show more</Text>
              </Button>
            </Box>
          )}
          {isExpanded && expandableAdvice && (
            <Text variant="text50" dangerouslySetInnerHTML={{ __html: expandableAdvice }} />
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default InvestableAssetsCard;
